import React, { useState, useEffect } from 'react'
import Client from 'shopify-buy'
import qs from 'query-string'

import Context from '~/context/StoreContext'

const client = Client.buildClient({
  storefrontAccessToken: process.env.GATSBY_SHOPIFY_ACCESS_TOKEN,
  domain: `typeandmore.dk`,
})

function getRandomNumber(min, max) {
  return parseInt(Math.random() * (max - min) + min)
}

const ContextProvider = ({ children, location }) => {
  const initialOptions = {
    character: 'A',
    style: 'Minimal',
    color: 'Peach',
    inverted: false,
    size: '30x40cm',
    scale: 75,
    personalisedText: '',
    personalisedText2: '',
    layout: 'standard',
    frame: 'Uden ramme',
  }

  let initialStoreState = {
    client,
    adding: false,
    checkout: { lineItems: [] },
    shop: {},
    selectedProductOptions: { ...initialOptions },
  }

  const [store, updateStore] = useState(initialStoreState)

  useEffect(() => {
    const initializeCheckout = async () => {
      // Check for an existing cart.
      const isBrowser = typeof window !== 'undefined'
      const existingCheckoutID = isBrowser
        ? localStorage.getItem('shopify_checkout_id')
        : null

      const setCheckoutInState = checkout => {
        if (isBrowser) {
          localStorage.setItem('shopify_checkout_id', checkout.id)
        }

        updateStore(prevState => {
          return { ...prevState, checkout }
        })
      }

      const createNewCheckout = () => store.client.checkout.create()
      const fetchCheckout = id => store.client.checkout.fetch(id)

      if (existingCheckoutID) {
        try {
          const checkout = await fetchCheckout(existingCheckoutID)
          // Make sure this cart hasn’t already been purchased.
          if (!checkout.completedAt) {
            setCheckoutInState(checkout)
            return
          }
        } catch (e) {
          localStorage.setItem('shopify_checkout_id', null)
        }
      }

      const newCheckout = await createNewCheckout()
      setCheckoutInState(newCheckout)
    }

    initializeCheckout()
  }, [store.client.checkout])

  return (
    <Context.Provider
      value={{
        store,
        resetFromQuerySting: () => {
          const query = qs.parse(location.search)

          if (!location.search) return false

          if (query['personalisedtext']) {
            Object.defineProperty(
              query,
              'personalisedText',
              Object.getOwnPropertyDescriptor(query, 'personalisedtext')
            )
            delete query['personalisedtext']
          }

          if (query['personalisedtext2']) {
            Object.defineProperty(
              query,
              'personalisedText2',
              Object.getOwnPropertyDescriptor(query, 'personalisedtext2')
            )
            delete query['personalisedtext2']
          }

          if (query['scale']) {
            query['scale'] = parseInt(query['scale'])
          }

          updateStore(prevState => ({
            ...prevState,
            ...{
              selectedProductOptions: {
                ...prevState.selectedProductOptions,
                ...query,
              },
            },
          }))

          return true
        },
        randomize: (products, styles) => {
          const {
            node: { title },
          } = products[getRandomNumber(0, 5)]

          const { characters, colors, layouts } = styles[title]

          const result = {
            character:
              characters[getRandomNumber(0, characters.length - 1)].value,
            style: title,
            color: colors[getRandomNumber(0, colors.length - 1)].value,
            inverted: Math.random() >= 0.5,
            scale: getRandomNumber(50, 201),
            layout: layouts[getRandomNumber(0, layouts.length - 1)].value,
          }

          updateStore(prevState => ({
            ...prevState,
            ...{
              selectedProductOptions: {
                ...prevState.selectedProductOptions,
                ...result,
              },
            },
          }))
        },
        resetOptions: () => {
          updateStore(prevState => ({
            ...prevState,
            ...{ selectedProductOptions: { ...initialOptions } },
          }))
        },
        handleProductOptionChange: (key, value) => {
          const result = { ...store.selectedProductOptions }

          result[key] = value

          if (value === '100x150cm') {
            result.frame = 'Uden ramme'
          }

          updateStore(prevState => ({
            ...prevState,
            ...{ selectedProductOptions: result },
          }))
        },
        addVariantToCart: (
          variantId,
          {
            character,
            color,
            scale,
            personalisedText,
            personalisedText2,
            layout,
            inverted,
            style,
            size,
          }
        ) => {
          if (variantId === '' && character === '' && color === '') {
            console.error('Character and color are required.')
            return
          }

          updateStore(prevState => {
            return { ...prevState, adding: true }
          })

          const { checkout, client } = store

          const checkoutId = checkout.id
          const lineItemsToUpdate = [
            {
              variantId,
              quantity: parseInt(1, 10),
              customAttributes: [
                { key: 'Character', value: character },
                { key: 'Color', value: color },
                { key: 'Scale', value: `${parseInt(scale)}%` },
                { key: 'Personalised text', value: personalisedText },
                { key: 'Personalised text 2', value: personalisedText2 },
                { key: 'Layout', value: layout },
                { key: 'Inverted', value: inverted.toString() },
                {
                  key: 'query',
                  value: qs.stringify({
                    character,
                    color,
                    scale,
                    personalisedText,
                    personalisedText2,
                    layout,
                    inverted,
                    style,
                    size,
                  }),
                },
              ],
            },
          ]

          return client.checkout
            .addLineItems(checkoutId, lineItemsToUpdate)
            .then(checkout => {
              updateStore(prevState => {
                return { ...prevState, checkout, adding: false }
              })
            })
        },
        removeLineItem: (client, checkoutID, lineItemID) =>
          client.checkout
            .removeLineItems(checkoutID, [lineItemID])
            .then(res => {
              updateStore(prevState => {
                return { ...prevState, checkout: res }
              })
            }),
        updateLineItem: (client, checkoutID, lineItemID, quantity) =>
          client.checkout
            .updateLineItems(checkoutID, [
              { id: lineItemID, quantity: parseInt(quantity, 10) },
            ])
            .then(res => {
              updateStore(prevState => {
                return { ...prevState, checkout: res }
              })
            }),
      }}
    >
      {children}
    </Context.Provider>
  )
}
export default ContextProvider
